// Client Engine CRM — application shell.
// Mounted at /clients/:id/crm/* as a nested layout, coexisting with the
// classic Layout and MailLayout. Renders tenant rail + black nav + topbar,
// and the active CRM screen through <Outlet>. Passes { clientId } down via
// Outlet context so child screens stay tenant-scoped.

import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useParams, useNavigate } from 'react-router-dom';
import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import CommandPalette from './CommandPalette';
import { useRole } from '../hooks/useRole';
import logo from '../assets/sonar-logo.svg';
import './crm.css';
import { CLIENT_PORTAL } from '../features';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Icon = ({ name }) => {
  const p = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const paths = {
    home: <><path d="M2.5 7.2L8 2.8l5.5 4.4"/><path d="M3.8 6.3v6.2a.7.7 0 0 0 .7.7h7a.7.7 0 0 0 .7-.7V6.3"/><path d="M6.5 13.2V9.5h3v3.7"/></>,
    contacts: <><circle cx="8" cy="5" r="2.6"/><path d="M3 13c0-2.8 2.2-4.2 5-4.2s5 1.4 5 4.2"/></>,
    companies: <><path d="M3 13.5V4.5L8 2.5l5 2v9"/><path d="M3 13.5h10"/><path d="M6 7h1.5M6 9.5h1.5M10 7h1.5M10 9.5h1.5"/></>,
    deals: <><path d="M8 2.2l5.2 3v5.6L8 13.8 2.8 10.8V5.2z"/><path d="M2.8 5.2L8 8.2l5.2-3M8 8.2v5.6"/></>,
    lists: <><path d="M5 4.2h8M5 8h8M5 11.8h8"/><circle cx="2.6" cy="4.2" r="0.9"/><circle cx="2.6" cy="8" r="0.9"/><circle cx="2.6" cy="11.8" r="0.9"/></>,
    inbox: <><path d="M2.5 8.5L4 3.5h8l1.5 5v3.5a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1z"/><path d="M2.5 8.5H6l1 1.5h2l1-1.5h3.5"/></>,
    deliverability: <><rect x="2.5" y="3" width="11" height="4" rx="1"/><rect x="2.5" y="9" width="11" height="4" rx="1"/><circle cx="5" cy="5" r=".6"/><circle cx="5" cy="11" r=".6"/></>,
    sequences: <><circle cx="3.5" cy="4" r="1.4"/><circle cx="3.5" cy="12" r="1.4"/><circle cx="12.5" cy="8" r="1.4"/><path d="M4.9 4H9a2 2 0 0 1 2 2v.7M4.9 12H9a2 2 0 0 0 2-2v-.7"/></>,
    campaigns: <><path d="M2.5 6.5v3l7 3V3.5z"/><path d="M9.5 5.5a2.5 2.5 0 0 1 0 5"/><path d="M2.5 9.5v2.5a1 1 0 0 0 2 0v-1.5"/></>,
    emailbatch: <><rect x="2.5" y="5" width="11" height="7.5" rx="1"/><path d="M2.5 6l5.5 4 5.5-4"/><path d="M4.5 3.5h7"/></>,
    linkedinbatch: <><rect x="2.5" y="2.5" width="11" height="11" rx="2"/><path d="M5 6.8v4"/><circle cx="5" cy="4.9" r="0.5"/><path d="M7.5 11V6.8M7.5 8.4a1.6 1.6 0 0 1 3.2 0V11"/></>,
    attribution: <><circle cx="4" cy="4" r="1.6"/><circle cx="4" cy="12" r="1.6"/><circle cx="12" cy="8" r="1.6"/><path d="M5.5 4.8l5 2.4M5.5 11.2l5-2.4"/></>,
    signals: <><path d="M2.5 8a5.5 5.5 0 0 1 5.5-5.5M13.5 8a5.5 5.5 0 0 1-5.5 5.5"/><path d="M5 8a3 3 0 0 1 3-3M11 8a3 3 0 0 1-3 3"/><circle cx="8" cy="8" r="1.1"/></>,
    metrics: <><path d="M2.5 13.5h11"/><rect x="3" y="8" width="2.2" height="4"/><rect x="6.9" y="5" width="2.2" height="7"/><rect x="10.8" y="9.5" width="2.2" height="2.5"/></>,
    discovery: <><circle cx="7" cy="7" r="4"/><path d="M10 10l3 3"/></>,
    icp: <><circle cx="8" cy="8" r="5.3"/><circle cx="8" cy="8" r="3"/><circle cx="8" cy="8" r="0.8"/></>,
    account: <><rect x="2.3" y="4" width="11.4" height="8" rx="1.2"/><path d="M2.3 6.5h11.4"/><path d="M4.5 9.5h2.5"/></>,
    usage: <><path d="M3 11a5 5 0 1 1 10 0"/><path d="M8 11l2.5-3"/><circle cx="8" cy="11" r="0.6"/></>,
    documents: <><path d="M4 2.5h5l3 3v8a.5.5 0 0 1-.5.5h-7.5a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5z"/><path d="M9 2.5v3h3"/><path d="M5.8 8.5h4.4M5.8 10.7h4.4"/></>,
    webhooks: <><circle cx="5" cy="5" r="1.8"/><path d="M6.2 6.4l2 3.2"/><circle cx="9" cy="11" r="1.8"/><path d="M10.6 10.2l1.8-3a1.8 1.8 0 1 0-3-1"/></>,
    admin: <path d="M8 2l5 2v4c0 3-2 5-5 6-3-1-5-3-5-6V4z"/>,
    exit: <><path d="M10 4L6 8l4 4"/><path d="M6 8h7"/></>,
  };
  if (!paths[name]) return null;
  return <svg className="crm-ic" viewBox="0 0 16 16" {...p}>{paths[name]}</svg>;
};

function TenantAvatar({ client, active, onClick, expanded }) {
  const [failed, setFailed] = useState(false);
  const initials = (client.name || '?')
    .split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const domain = client.website
    ? client.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
    : null;
  const showImg = domain && !failed;
  const avatar = (
    <button
      className={'crm-ws' + (active ? ' crm-ws--active' : '')}
      onClick={onClick}
      style={!showImg ? { background: 'linear-gradient(135deg,#FF7A59,#EC4899)' } : undefined}
    >
      {showImg
        ? <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} alt="" onError={() => setFailed(true)} />
        : initials}
    </button>
  );
  if (expanded) {
    return (
      <div className="crm-ws-row" onClick={onClick} style={{ cursor: 'pointer' }}>
        {avatar}
        <span className="crm-ws-name">{client.name}</span>
      </div>
    );
  }
  return <CollapsedAvatar avatar={avatar} name={client.name} />;
}

function CollapsedAvatar({ avatar, name }) {
  const [tip, setTip] = useState(null);
  const show = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ x: r.right + 10, y: r.top + r.height / 2 });
  };
  const hide = () => setTip(null);
  return (
    <div className="crm-ws-wrap" onMouseEnter={show} onMouseLeave={hide}>
      {avatar}
      {tip && createPortal(
        <div className="crm-ws-portal-tip" style={{ left: tip.x, top: tip.y }}>{name}</div>,
        document.body
      )}
    </div>
  );
}

export default function CrmShell() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const [userMenu, setUserMenu] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen(true); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const { clientId: roleClientId, isWebciteStaff } = useRole();

  const clientId = id || roleClientId;

  const [clients, setClients] = useState([]);
  const [railExpanded, setRailExpanded] = useState(false);
  const [client, setClient] = useState(null);

  const userInitials = (user?.firstName || user?.fullName || 'JB')
    .split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  useEffect(() => {
    if (!clientId) { setClient(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok && !cancelled) setClient(await r.json());
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [clientId, getToken]);

  const loadClients = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/clients`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setClients(await r.json());
    } catch (_) {}
  }, [getToken]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const operator = !clientId;
  const base = operator ? `/crm` : `/clients/${clientId}/crm`;
  const railClients = isWebciteStaff ? clients : (client ? [client] : []);

  return (
    <div className={"crm-root" + (railExpanded ? " crm-rail-expanded" : "")}>
      <div className="crm-rail">
        {isWebciteStaff && (
          <button className="crm-ws" title="All clients" onClick={() => navigate(`${base}/clients`)} style={{ background: '#1E1E22', color: '#FF7A59' }}>
            <Icon name="admin" />
          </button>
        )}
        {isWebciteStaff && <div className="crm-rail-sep" />}
        {railClients.map(c => (
          <TenantAvatar
            key={c.id}
            client={c}
            active={String(c.id) === String(clientId)}
            onClick={() => navigate(`/clients/${c.id}/crm`)}
            expanded={railExpanded}
          />
        ))}
        {isWebciteStaff && (
          <>
            <div className="crm-rail-sep" />
            <button className="crm-rail-add" title="All clients" onClick={() => navigate(`${base}/clients`)}>+</button>
          </>
        )}
        <button className="crm-rail-toggle" title={railExpanded ? 'Collapse' : 'Expand'} onClick={() => setRailExpanded(v => !v)}>
          {railExpanded ? '\u00AB' : '\u00BB'}
        </button>
      </div>

      <nav className="crm-nav">
        <div className="crm-brand"><img src={logo} alt="Sonar" /></div>

        {isWebciteStaff && <div className="crm-nav-grp">Operator</div>}
        {isWebciteStaff && <NavLink to={`${base}/clients`} className={({ isActive }) => 'crm-nav-item crm-do' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="companies" />Clients</NavLink>}
        {isWebciteStaff && operator && <NavLink to={`${base}/home`} className={({ isActive }) => 'crm-nav-item crm-do' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="home" />Home</NavLink>}
        {isWebciteStaff && <NavLink to={`${base}/attribution`} className={({ isActive }) => 'crm-nav-item crm-do' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="attribution" />Attribution</NavLink>}

        {!operator && (<>
        <div className="crm-nav-grp">CRM</div>
        <NavLink to={`${base}/home`} className={({ isActive }) => 'crm-nav-item crm-do' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="home" />Home</NavLink>
        <NavLink to={`${base}/contacts`} className={({ isActive }) => 'crm-nav-item crm-db' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="contacts" />Contacts</NavLink>
        <NavLink to={`${base}/companies`} className={({ isActive }) => 'crm-nav-item crm-dv' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="companies" />Companies</NavLink>
        <NavLink to={`${base}/opportunities`} className={({ isActive }) => 'crm-nav-item crm-da' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="deals" />Opportunities</NavLink>
        <NavLink to={`${base}/lists`} className={({ isActive }) => 'crm-nav-item crm-dc' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="lists" />Lists</NavLink>

        <div className="crm-nav-grp">Outreach</div>
        <NavLink to={`${base}/inbox`} className={({ isActive }) => 'crm-nav-item crm-db' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="inbox" />Inbox</NavLink>
        <NavLink to={`${base}/sequences`} className={({ isActive }) => 'crm-nav-item crm-dp' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="sequences" />Sequences</NavLink>
        <NavLink to={`${base}/campaigns`} className={({ isActive }) => 'crm-nav-item crm-dc' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="campaigns" />Campaigns</NavLink>
        <NavLink to={`${base}/deliverability`} className={({ isActive }) => 'crm-nav-item crm-db' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="deliverability" />Deliverability</NavLink>
        <NavLink to={`${base}/email-batches`} className={({ isActive }) => 'crm-nav-item crm-db' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="emailbatch" />Email Batches</NavLink>
        <NavLink to={`${base}/linkedin-batches`} className={({ isActive }) => 'crm-nav-item crm-db' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="linkedinbatch" />LinkedIn Batches</NavLink>

        <div className="crm-nav-grp">Intelligence</div>
        <NavLink to={`${base}/market`} className={({ isActive }) => 'crm-nav-item crm-do' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="metrics" />Market</NavLink>
        <NavLink to={`${base}/intelligence`} className={({ isActive }) => 'crm-nav-item crm-dr' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="discovery" />Intelligence</NavLink>
        <NavLink to={`${base}/performance`} className={({ isActive }) => 'crm-nav-item crm-dv' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="metrics" />Performance</NavLink>

        <div className="crm-nav-grp">Settings</div>
        <NavLink to={`${base}/profile`} className={({ isActive }) => 'crm-nav-item crm-dr' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="icp" />Client profile</NavLink>
        {CLIENT_PORTAL && (<>
        <NavLink to={`${base}/account`} className={({ isActive }) => 'crm-nav-item crm-db' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="account" />Account</NavLink>
        <NavLink to={`${base}/usage`} className={({ isActive }) => 'crm-nav-item crm-da' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="usage" />Usage</NavLink>
        <NavLink to={`${base}/documents`} className={({ isActive }) => 'crm-nav-item crm-ds' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="documents" />Documents</NavLink>
        <NavLink to={`${base}/webhooks`} className={({ isActive }) => 'crm-nav-item crm-dv' + (isActive ? ' crm-nav-item--active' : '')}><Icon name="webhooks" />Webhooks</NavLink>
        </>)}
        </>)}

      </nav>

      <div className="crm-main">
        <div className="crm-topbar">
          <div className="crm-crumbs">
            {client ? <b>{client.name}</b> : 'Workspace'}
          </div>
          <button type="button" className="crm-search" onClick={() => setPaletteOpen(true)}>⌘K&nbsp;&nbsp;Search anything</button>
          <div className="crm-usermenu">
            <button type="button" className="crm-av crm-av-btn" onClick={() => setUserMenu(v => !v)} title="Account">{userInitials}</button>
            {userMenu ? (
              <>
                <div className="crm-usermenu-backdrop" onClick={() => setUserMenu(false)} />
                <div className="crm-usermenu-pop">
                  <div className="crm-usermenu-who">
                    <strong>{user?.fullName || user?.firstName || 'Signed in'}</strong>
                    <span>{user?.primaryEmailAddress?.emailAddress || ''}</span>
                  </div>
                  <button type="button" className="crm-usermenu-item" onClick={() => { setUserMenu(false); signOut(); }}>Sign out</button>
                </div>
              </>
            ) : null}
          </div>
        </div>
        <div className="crm-outlet">
          <Outlet context={{ clientId, client }} />
        </div>
      </div>
      {paletteOpen ? <CommandPalette clientId={clientId} isStaff={isWebciteStaff} onClose={() => setPaletteOpen(false)} /> : null}
    </div>
  );
}
